/**
 * chat-director/thread — weave loose beats into coherent, multi-turn topics.
 *
 * The town reads as genuinely *conversational* only when a batch is a back-and-forth
 * about ONE thing — a show-off and the admiration it earns, a question and the answer,
 * the player's mon and the warm reactions to it — rather than a stack of unrelated
 * one-liners. This module groups beats into THREADS keyed by what they're *about*, so
 * the speaker-selection + voice layers can phrase a believable exchange.
 *
 * It is pure + deterministic (no rng, no clock): a stable function of the beats.
 * It NEVER invents canon — it only re-orders + clusters the ground-truth beats so the
 * voice has a coherent topic to phrase.
 */
import type { Beat } from '@/shared';

/** The conversational subject a beat hangs off — its thread key. */
export function threadKeyOf(beat: Beat): string {
  const d = beat.data;
  // Two-way: anything ABOUT the player's mon threads with the player's show-off.
  if (d.byPlayer === true || d.aboutPlayerMon === true) {
    const sp = typeof d.speciesId === 'string' ? d.speciesId : '';
    return `player-mon:${sp}`;
  }
  // Admiration of a peer threads with that peer's own show-off.
  if (typeof d.aboutTrainerId === 'string') {
    const sp = typeof d.aboutSpeciesId === 'string' ? d.aboutSpeciesId : '';
    return `mon:${d.aboutTrainerId}:${sp}`;
  }
  // A trainer showing off their own mon anchors a thread others can admire into.
  if (d.topic === 'show-off') {
    const sp = typeof d.speciesId === 'string' ? d.speciesId : '';
    return `mon:${beat.subjectTrainerId}:${sp}`;
  }
  // Topical culture (know-how, storyline, rumor) threads by its topic + who raised it.
  if (typeof d.topic === 'string' && d.topic !== 'pottering') {
    if (d.topic === 'storyline' && typeof d.storyline === 'string') {
      return `story:${d.storyline}`;
    }
    return `topic:${d.topic}:${beat.subjectTrainerId}`;
  }
  if (beat.kind === 'lore-rumor') return `rumor:${beat.subjectTrainerId}`;
  // Real events (catch/evolution/trade/…) thread around the subject they concern.
  return `event:${beat.subjectTrainerId}:${beat.kind}`;
}

/** A coherent conversational thread: a topic key + the beats that belong to it. */
export interface BeatThread {
  readonly key: string;
  readonly beats: readonly Beat[];
  /** True when the player started or is the subject of this thread (two-way). */
  readonly involvesPlayer: boolean;
}

/**
 * Whether a beat is the ANCHOR of its thread — the thing being talked about (a
 * show-off, the player's own line, a real event) — as opposed to a reaction to it
 * (admiration, a question about the player's mon). Anchors lead the exchange so a
 * thread reads naturally: the proud line first, then the warm replies.
 */
function isReaction(beat: Beat): boolean {
  const d = beat.data;
  return (
    d.aboutPlayerMon === true ||
    typeof d.aboutTrainerId === 'string' ||
    (typeof d.question === 'string' && d.byPlayer !== true) ||
    (typeof d.compliment === 'string' && d.topic !== 'show-off')
  );
}

/** Order a thread's beats anchor-first, otherwise stable (preserves incoming order). */
function orderWithinThread(beats: readonly Beat[]): Beat[] {
  return beats
    .map((beat, i) => ({ beat, i }))
    .sort((a, b) => {
      const ra = isReaction(a.beat) ? 1 : 0;
      const rb = isReaction(b.beat) ? 1 : 0;
      if (ra !== rb) return ra - rb;
      return a.i - b.i; // stable
    })
    .map((x) => x.beat);
}

/**
 * Cluster beats into threads, preserving the first-seen order of the threads (so a
 * caller that pre-orders beats loudest-first keeps that ordering across threads).
 * Beats inside a thread keep their incoming order — a show-off lands before the
 * admiration replies that follow it, so the exchange reads naturally.
 */
export function threadBeats(beats: readonly Beat[]): BeatThread[] {
  const byKey = new Map<string, Beat[]>();
  const order: string[] = [];
  for (const beat of beats) {
    const key = threadKeyOf(beat);
    let bucket = byKey.get(key);
    if (!bucket) {
      bucket = [];
      byKey.set(key, bucket);
      order.push(key);
    }
    bucket.push(beat);
  }
  return order.map((key) => {
    const threadBeats = orderWithinThread(byKey.get(key)!);
    const involvesPlayer =
      key.startsWith('player-mon:') ||
      threadBeats.some(
        (b) =>
          b.subjectTrainerId === 'player' ||
          (b.involvedTrainerIds ?? []).includes('player') ||
          b.data.byPlayer === true ||
          b.data.aboutPlayerMon === true,
      );
    return { key, beats: threadBeats, involvesPlayer };
  });
}

/** Normalize a line of dialogue for near-identical comparison (dedup). */
export function normalizeLine(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ') // drop punctuation/usernames-as-punctuation noise
    .replace(/\s+/g, ' ')
    .trim();
}
